import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { type Nullable } from '@Types';
import { type AdmissionOffer } from 'AdmissionOffer';
import { type CohortSectionOffer } from 'CohortSectionOffer';
import { formatCurrencyForDisplay } from 'Currency';
import { paymentSituationWithFormattedValues } from 'PaymentSituation';
import { getProgramConfigValue } from 'Program';
import { isFullScholarship, isPartialScholarship } from 'Scholarship';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { getCohort } from 'Users';
import { type Cohort } from 'Cohorts';
import { LOCALE_PREFIX } from '../../tuition_and_registration/constants';

const SCHOLARSHIP_PREFIX = 'scholarship_labels';

// Scholarship labels that the admission offer's meritLabelOverride can replace.
const OVERRIDABLE_SCHOLARSHIP_LABELS = ['strada', 'zenith'];

type Props = {
    admissionOffer: AdmissionOffer;
    cohortSectionOffer: CohortSectionOffer;
    className?: string;
};

type LedgerRow = {
    key: string;
    title: string;
    costOrDiscount: string;
    className: string;
};

/*
    The amounts use text-grey-darker because they are the main content. The row class names are spec
    selectors and carry no styles.
*/
export function CohortRegistrationTuitionLedger({
    admissionOffer,
    cohortSectionOffer,
    className,
}: Props): ReactElement {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();
    const cohort = getCohort(currentUser);

    const rows = getLedgerRows({ admissionOffer, cohortSectionOffer, cohort, t });
    const singleRow = rows.length === 1;

    return (
        <div
            className={twMerge('flex flex-col border-t border-slate-grey-200 py-5 text-xs text-grey-darker', className)}
        >
            {rows.map((row, index) => (
                <div
                    key={row.key}
                    className={[
                        'ledger-row',
                        row.className,
                        'flex justify-between gap-x-4 max-sm:items-end',
                        !singleRow && index === 0 ? 'mb-2.5 sm:mb-5' : '',
                        !singleRow && index > 0 && index < rows.length - 1 ? 'mb-2.5' : '',
                        // The total sits under a rule of its own, and is the one emphasised row.
                        row.key === 'total-tuition' ? 'mt-1 border-t border-grey-darker pt-3.5 font-semibold' : '',
                        singleRow ? 'font-semibold' : '',
                    ].join(' ')}
                >
                    <span className="title">{row.title}</span>
                    <span className="cost-or-discount whitespace-nowrap text-end">{row.costOrDiscount}</span>
                </div>
            ))}
        </div>
    );
}

type LedgerInputs = Props & {
    cohort: Cohort | null;
    t: ReturnType<typeof useTranslation>['t'];
};

function getLedgerRows({ admissionOffer, cohortSectionOffer, cohort, t }: LedgerInputs): LedgerRow[] {
    const hasPartialScholarship = isPartialScholarship(cohortSectionOffer.cumulativeOfferedScholarship);
    const hasFullScholarship = isFullScholarship(cohortSectionOffer.cumulativeOfferedScholarship);
    const hasNoScholarship = !hasPartialScholarship && !hasFullScholarship;

    const basePaymentSituation = paymentSituationWithFormattedValues(cohortSectionOffer.basePaymentSituation);

    const cost = (value: string) => t(`${LOCALE_PREFIX}.cost_of_ledger_item`, { value });

    const rows: LedgerRow[] = [
        {
            key: 'base-tuition',
            title: t(`${LOCALE_PREFIX}.${baseTuitionKey({ admissionOffer, hasNoScholarship })}`, {
                programTitle: fullProgramTitle(admissionOffer, cohort),
            }),
            costOrDiscount: cost(formatCurrencyForDisplay(basePaymentSituation.paymentInformation.baseTuition)),
            className: 'base-tuition-row',
        },
    ];

    if (hasNoScholarship) return rows;

    cohortSectionOffer.offeredScholarships?.forEach(scholarship => {
        rows.push({
            key: `scholarship-${scholarship.id}`,
            title: scholarshipTitle(scholarship, admissionOffer, t),
            costOrDiscount: t(`${LOCALE_PREFIX}.discount_of_ledger_item`, {
                value: formatCurrencyForDisplay(scholarship.amount ?? undefined),
            }),
            className: 'scholarship-row',
        });
    });

    rows.push({
        key: 'total-tuition',
        title: t(`${LOCALE_PREFIX}.total_tuition`),
        costOrDiscount: cost(
            hasFullScholarship
                ? '0'
                : formatCurrencyForDisplay(basePaymentSituation.paymentInformation.netRequiredPayment),
        ),
        className: 'total-tuition-row',
    });

    return rows;
}

/*
    Only ExecEd programs hide the program title in the base tuition row. If this has to cover more
    program types, a ProgramTypeConfigs flag would be a better way to control it.
*/
function baseTuitionKey({
    admissionOffer,
    hasNoScholarship,
}: {
    admissionOffer: AdmissionOffer;
    hasNoScholarship: boolean;
}): string {
    if (hasNoScholarship) return 'base_tuition_no_scholarship';
    if (getProgramConfigValue<boolean>(admissionOffer.programType, 'isExecEd')) return 'base_tuition_no_program_title';
    return 'base_tuition';
}

function fullProgramTitle(admissionOffer: AdmissionOffer, cohort: Cohort | null): string {
    const getFullTitle = getProgramConfigValue<((cohort: Cohort | null) => string) | null>(
        admissionOffer.programType,
        'fullTitle',
    );
    return getFullTitle?.(cohort) ?? '';
}

/*
    A scholarship label has its own locale key, and falls back to `base_scholarship` when it does not:
    i18next takes an array and uses the first key that exists. Not every label interpolates the year,
    but passing it always is harmless.
*/
function scholarshipTitle(
    scholarship: { label: Nullable<string> },
    admissionOffer: AdmissionOffer,
    t: LedgerInputs['t'],
): string {
    if (
        scholarship.label &&
        OVERRIDABLE_SCHOLARSHIP_LABELS.includes(scholarship.label) &&
        admissionOffer.meritLabelOverride
    ) {
        return admissionOffer.meritLabelOverride;
    }

    // A scholarship with no label goes straight to the generic fallback.
    const labelKey = scholarship.label
        ? `${SCHOLARSHIP_PREFIX}.${scholarship.label}`
        : `${SCHOLARSHIP_PREFIX}.base_scholarship`;

    return t([labelKey, `${SCHOLARSHIP_PREFIX}.base_scholarship`], {
        offeredYear: admissionOffer.offeredAt ? new Date(admissionOffer.offeredAt * 1000).getFullYear() : '',
    });
}
